import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';
import { ForkliftInspectionListItem } from '../forklift-inspection/forklift-inspection-list-item.model';

export class TrenchingExcavationWorkPlan {
  id?: string;
  submittedByName: string = '';
  submittedByEmail: string = '';
  submissionDate?: Date;

  // Step 1 — General Information
  employerName: string = '';
  date: Date | null = null;
  jobsiteNameAddress: string = '';
  competentPersonName: string = '';
  utilityLocatesObtained: string = '';
  authorityToStopWork: string = '';

  // Step 2 — Site Conditions & Hazards
  hydraulicShoringInstalled: boolean = false;
  hydraulicShoringMfr: string = '';
  hydraulicShoringSerialNumber: string = '';
  trenchBox: boolean = false;
  engineeredSystem: boolean = false;
  engineeredSystemEngineerName: string = '';
  soilClassification: string = '';
  undergroundGas: boolean = false;
  undergroundWater: boolean = false;
  undergroundSewer: boolean = false;
  undergroundElectric: boolean = false;
  undergroundCommunications: boolean = false;
  undergroundOther: boolean = false;
  undergroundOtherDescription: string = '';
  undergroundInstallationProtectionMethod: string = '';
  continuousAtmosphericMonitoring: boolean = false;
  ventilationFans: string = '';
  ventilationFansOther: string = '';
  surfaceEncumbranceProtectionMethod: string = '';
  adjacentStructureStabilizationMethod: string = '';
  hazardProtectionMethod: string = '';

  // Step 3 — Access, Shoring & Completion
  accessLadder: boolean = false;
  accessTaperSlope: boolean = false;
  accessStairs: boolean = false;
  accessEgressOtherDescription: string = '';
  shoringPlanDescription: string = '';
  // rpeStampedDrawing: boolean = false;
  // soilsEngineerApproval: boolean = false;
  inspectionsAcknowledged: boolean = false;
  trainedEmployees: string = '';

  constructor(data?: Partial<TrenchingExcavationWorkPlan>) {
    if (data) {
      Object.assign(this, {
        ...data,
        date: data.date ? parseLocalDate(data.date as any) : null,
        submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
      });
    }
  }

  static getFormInfo(): GeneralFormData {
    return {
      formType: 'trenching-excavation-work-plan',
      formTitle: 'Trenching & Excavation Evaluation & Work Plan',
      formIcon: 'construct-outline',
      cardImage: 'assets/form-card-tiles/trenching-excavation-work-plan.png',
      formAttachmentRequirements: {},
    };
  }

  static getFormSteps(): FormStep[] {
    return [
      {
        id: 'general-info',
        shortTitle: 'General Info',
        title: 'General Information',
        icon: 'information-circle-outline',
        description: 'Enter general job and site information',
        sections: [
          {
            fields: [
              'employerName',
              'date',
              'jobsiteNameAddress',
              'competentPersonName',
              'utilityLocatesObtained',
              'authorityToStopWork',
            ],
          },
        ],
      },
      {
        id: 'site-conditions',
        shortTitle: 'Site Conditions',
        title: 'Site Conditions & Hazards',
        icon: 'construct-outline',
        description: 'Identify equipment, soil classification, underground installations, atmospheric hazards, and protection methods',
        sections: [
          {
            title: 'Equipment Used',
            checkboxGroups: [
              {
                fields: ['hydraulicShoringInstalled', 'trenchBox', 'engineeredSystem'],
                checkboxLabel: this.getFieldMappings()['equipmentUsed'],
              },
            ],
            fields: ['hydraulicShoringMfr', 'hydraulicShoringSerialNumber', 'engineeredSystemEngineerName'],
          },
          {
            title: 'Soil Classification',
            fields: ['soilClassification'],
          },
          {
            title: 'Underground Installations',
            description: 'Locates are only valid for 45 days.',
            checkboxGroups: [
              {
                fields: [
                  'undergroundGas',
                  'undergroundWater',
                  'undergroundSewer',
                  'undergroundElectric',
                  'undergroundCommunications',
                  'undergroundOther',
                ],
                checkboxLabel: this.getFieldMappings()['undergroundInstallations'],
              },
            ],
            fields: ['undergroundOtherDescription', 'undergroundInstallationProtectionMethod'],
          },
          {
            title: 'Hazardous Atmospheres',
            checkboxGroups: [
              {
                fields: ['continuousAtmosphericMonitoring'],
                checkboxLabel: this.getFieldMappings()['atmosphericHazards'],
              },
            ],
            fields: ['ventilationFans', 'ventilationFansOther'],
          },
          {
            title: 'Surface & Hazard Protection',
            fields: [
              'surfaceEncumbranceProtectionMethod',
              'adjacentStructureStabilizationMethod',
              'hazardProtectionMethod',
            ],
          },
        ],
      },
      {
        id: 'access-shoring-completion',
        shortTitle: 'Access & Shoring',
        title: 'Access, Shoring & Completion',
        icon: 'list-circle-outline',
        description: 'Document trench access, shoring plan, and complete inspections and training acknowledgements',
        sections: [
          {
            title: 'Trench Access / Egress',
            checkboxGroups: [
              {
                fields: ['accessLadder', 'accessTaperSlope', 'accessStairs'],
                checkboxLabel: this.getFieldMappings()['trenchAccess'],
              },
            ],
            fields: ['accessEgressOtherDescription'],
          },
          {
            title: 'Shoring Plan',
            fields: ['shoringPlanDescription', 
              // 'rpeStampedDrawing', 'soilsEngineerApproval'
            ],
          },
          {
            title: 'Inspections',
            fields: ['inspectionsAcknowledged'],
          },
          {
            title: 'Training',
            fields: ['trainedEmployees'],
          },
        ],
      },
    ];
  }

  static getFieldMappings(): Record<string, string> {
    return {
      submittedByName: 'Submitter Name',
      submittedByEmail: 'Submitter Email',
      submissionDate: 'Submission Date',

      // Step 1
      employerName: "Employer's Name",
      date: 'Date',
      jobsiteNameAddress: 'Jobsite Name/Address',
      competentPersonName: 'Name of competent person remaining onsite during the excavation',
      utilityLocatesObtained: 'Did you obtain utility locates for the area of the excavation?',
      authorityToStopWork:
        'Do you have authority to stop work until hazards are corrected, eliminated or controlled and to remove employees from the hazardous area until proper systems are in place?',

      // Step 2 — Equipment
      equipmentUsed: 'Equipment Used',
      hydraulicShoringInstalled: 'Hydraulic shoring installed per MFG requirements',
      hydraulicShoringMfr: 'Hydraulic Shoring MFR',
      hydraulicShoringSerialNumber: 'Hydraulic shoring Serial #',
      trenchBox: 'Trench box',
      engineeredSystem: 'Engineered System',
      engineeredSystemEngineerName: 'Name of engineer',

      // Soil Classification
      soilClassification: 'Soil Classification',

      // Underground Installations
      undergroundInstallations: 'Underground Installations',
      undergroundGas: 'Gas',
      undergroundWater: 'Water',
      undergroundSewer: 'Sewer',
      undergroundElectric: 'Electric',
      undergroundCommunications: 'Communications',
      undergroundOther: 'Other',
      undergroundOtherDescription: 'Other underground installations (specify)',
      undergroundInstallationProtectionMethod:
        'How will underground installations be protected when the trench is open?',

      // Hazardous Atmospheres
      atmosphericHazards: 'Hazardous Atmospheres',
      continuousAtmosphericMonitoring: 'Continuous monitoring for Oxygen, H2S, CO and LEL',
      ventilationFans: 'Ventilation fans',
      ventilationFansOther: 'Ventilation fans — Other description',

      // Surface & Hazard Protection
      surfaceEncumbranceProtectionMethod: 'Describe the method of protection from surface encumbrances',
      adjacentStructureStabilizationMethod: 'Describe the method of stabilizing adjacent structures',
      hazardProtectionMethod:
        'Describe the method of protection from water accumulation, loose soil, rock, or equipment that could pose a hazard by falling or rolling into the excavation',

      // Step 3
      trenchAccess: 'Trench Access / Egress',
      accessLadder: "Ladder placed every 25'",
      accessTaperSlope: "Taper or slope suitable for walk-out every 25'",
      accessStairs: 'Stairs',
      accessEgressOtherDescription: 'If access/egress is something other than the above, describe',
      shoringPlanDescription: "Describe system(s) used and its installation, removal, and protective measures",
      // rpeStampedDrawing: "RPE stamped drawing obtained (required for excavations > 20' deep)",
      // soilsEngineerApproval: 'Soils engineer indicating trench work is safe',
      inspectionsAcknowledgedLabel: "Inspections",
      inspectionsAcknowledged:
        'Inspections of trench/excavation, protective system and adjacent area will be completed before work, during work, after rain, vibration and any other hazardous condition',
    trainingAcknowledgementLabel:"Training",
        trainingAcknowledgement:
        'Employees listed below have received training on the Trenching & Excavation Work Plan for the project listed above',
      trainedEmployees: 'Trained employee names',
    };
  }
}
